import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
export interface NumberLineArrow {
  from: number;
  to: number;
  label?: string;
}
export interface NumberLineProps {
  start: number;
  end: number;
  highlight?: number[];
  step?: number;
  label?: string;
  mode?: 'stimulus' | 'support';
  tickLabels?: string[];
  fractionLabels?: Record<string, string>;
  /** Optional arrows drawn above the line, e.g. for negative-number jumps. */
  arrows?: NumberLineArrow[];
}
function isFiniteNumber(n: unknown): n is number {
  return typeof n === 'number' && Number.isFinite(n);
}
function formatTick(value: number, maxFractionDigits: number) {
  const nf = new Intl.NumberFormat('nl-NL', {
    maximumFractionDigits: maxFractionDigits,
    minimumFractionDigits: 0,
    notation: 'standard'
  });
  return nf.format(value);
}
function calculateNiceStep(range: number): number {
  if (range === 0) return 1;
  const rawStep = range / 10;
  const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const normalizedStep = rawStep / magnitude;
  let niceStep: number;
  if (normalizedStep < 1.5) niceStep = 1;else if (normalizedStep < 3.5) niceStep = 2;else if (normalizedStep < 7.5) niceStep = 5;else niceStep = 10;
  return Number((niceStep * magnitude).toPrecision(10));
}
function getTickLabel(value: number, maxFractionDigits: number, tickLabels: string[] | undefined, tickIndex: number, fractionLabels: Record<string, string> | undefined): string {
  if (tickLabels && tickIndex < tickLabels.length) {
    return tickLabels[tickIndex];
  }
  if (fractionLabels) {
    for (const [key, label] of Object.entries(fractionLabels)) {
      const keyNum = parseFloat(key);
      if (!isNaN(keyNum) && Math.abs(keyNum - value) < 0.001) {
        return label;
      }
    }
  }
  return formatTick(value, maxFractionDigits);
}
/** Project a value onto the [0,100]% axis. */
function projectPct(value: number, min: number, max: number): number {
  if (max === min) return 50;
  return (value - min) / (max - min) * 100;
}
export function NumberLine({
  start,
  end,
  highlight = [],
  step,
  label,
  mode = 'support',
  tickLabels,
  fractionLabels,
  arrows
}: NumberLineProps) {
  const computed = useMemo(() => {
    const validInputs = isFiniteNumber(start) && isFiniteNumber(end);
    if (!validInputs) return null;
    const min = Math.min(start, end);
    const max = Math.max(start, end);
    const range = max - min;
    let stepSize: number;
    if (isFiniteNumber(step) && step > 0 && range > 0) {
      stepSize = step;
    } else if (range > 0) {
      stepSize = calculateNiceStep(range);
    } else {
      stepSize = 1;
    }
    let steps = range === 0 ? 0 : Math.round(range / stepSize);
    if (steps > 50) {
      stepSize = calculateNiceStep(range);
      steps = Math.round(range / stepSize);
    }
    const maxFractionDigits = (() => {
      // F-NUMBERLINE-PRECISION (mei 2026): the prior tiered cutoffs rounded
      // 0.25 → "0,3", 0.5 → "0,5", 0.75 → "0,8" on a fraction number-line
      // because stepSize 0.25 fell into the 1-digit bucket. Inspect the actual
      // stepSize and pick the smallest digit count that represents it exactly.
      if (stepSize >= 1) return 0;
      const stepStr = stepSize.toPrecision(10).replace(/0+$/, '');
      const dotIdx = stepStr.indexOf('.');
      if (dotIdx === -1) return 0;
      return Math.min(6, stepStr.length - dotIdx - 1);
    })();
    const ticks = range !== 0 ? Array.from({
      length: steps + 1
    }, (_, i) => Number((min + i * stepSize).toPrecision(10))) : [];
    return {
      min,
      max,
      range,
      steps,
      stepSize,
      maxFractionDigits,
      ticks
    };
  }, [start, end, step]);
  const cleanedHighlights = useMemo(() => highlight.filter(isFiniteNumber), [highlight]);
  const cleanedArrows = useMemo(() => (arrows ?? []).filter(a => a && isFiniteNumber(a.from) && isFiniteNumber(a.to) && a.from !== a.to), [arrows]);
  if (!computed) {
    return <div className="bg-red-50 p-6 rounded-2xl border-2 border-red-200 text-center" data-id="element-12180">
        <p className="text-red-600 font-medium" data-id="element-12181">
          Ongeldige getallenlijn configuratie
        </p>
      </div>;
  }
  if (computed.range === 0) {
    return <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-2xl border-2 border-blue-200" aria-label="Getallenlijn met één waarde" data-id="element-12182">
        {label && <div className="text-sm font-bold text-blue-900 mb-4 text-center" data-id="element-12183">
            {label}
          </div>}
        <div className="text-center text-slate-700 text-2xl font-bold" data-id="element-12184">
          {formatTick(computed.min, 6)}
        </div>
      </div>;
  }
  const labelForTick = (value: number, index: number) => getTickLabel(value, computed.maxFractionDigits, tickLabels, index, fractionLabels);
  // Format an arbitrary value (may not be on a tick) using max precision of the line
  const formatValue = (value: number) => {
    // Use more digits than the tick step so decimal marks like 2.749 still display correctly
    const valueDigits = (() => {
      const s = String(value);
      const dot = s.indexOf('.');
      return dot === -1 ? 0 : Math.min(s.length - dot - 1, 6);
    })();
    const digits = Math.max(computed.maxFractionDigits, valueDigits);
    return formatTick(value, digits);
  };
  // Clamp a value into [min, max] for safe projection
  const clamp = (v: number) => Math.min(computed.max, Math.max(computed.min, v));
  const isNegative = computed.min < 0;
  const isDecimal = computed.stepSize % 1 !== 0;
  let explanationTitle = 'Wat zie je hier?';
  let explanationText = `Deze getallenlijn loopt van ${labelForTick(computed.min, 0)} tot ${labelForTick(computed.max, computed.steps)}. Elk streepje is ${formatTick(computed.stepSize, computed.maxFractionDigits)} verder.`;
  if (isNegative) {
    explanationTitle = 'Getallen onder nul';
    explanationText = `Deze getallenlijn gaat onder nul. Hoe verder naar links, hoe kleiner het getal. Elk streepje is ${formatTick(computed.stepSize, computed.maxFractionDigits)} verder.`;
  } else if (isDecimal) {
    explanationTitle = 'Kommagetallen';
    explanationText = `Deze getallenlijn zoomt in op de stukjes tussen hele getallen. Elk streepje is ${formatTick(computed.stepSize, computed.maxFractionDigits)} verder.`;
  }
  // ── Number line visualization ────────────────────────────────────────────
  const numberLineViz = <div className="relative h-32" aria-label="Getallenlijn" data-id="element-12185">
      {/* Arrows above the line */}
      {cleanedArrows.length > 0 && <div className="absolute top-0 left-0 right-0 h-10" data-id="element-12186">
          {cleanedArrows.map((arrow, ai) => {
        const fromPct = projectPct(clamp(arrow.from), computed.min, computed.max);
        const toPct = projectPct(clamp(arrow.to), computed.min, computed.max);
        const left = Math.min(fromPct, toPct);
        const width = Math.abs(toPct - fromPct);
        const direction = toPct >= fromPct ? 'right' : 'left';
        return <motion.div key={`arrow-${ai}`} initial={{
          opacity: 0,
          scaleX: 0
        }} animate={{
          opacity: 1,
          scaleX: 1
        }} transition={{
          delay: 0.15,
          type: 'spring',
          stiffness: 300,
          damping: 24
        }} className="absolute top-2 h-6" style={{
          left: `${left}%`,
          width: `${width}%`,
          transformOrigin: direction === 'right' ? 'left center' : 'right center'
        }} data-id="element-12187">
                {/* Arc */}
                <div className="absolute inset-x-0 top-3 h-3 border-t-2 border-l-2 border-r-2 border-indigo-500 rounded-t-full" data-id="element-12188" />
                {/* Arrowhead */}
                <div className={`absolute top-1.5 ${direction === 'right' ? 'right-0' : 'left-0'} w-0 h-0 border-y-[5px] border-y-transparent ${direction === 'right' ? 'border-l-[8px] border-l-indigo-500' : 'border-r-[8px] border-r-indigo-500'}`} data-id="element-12189" />
                {/* Label */}
                {arrow.label && <div className="absolute -top-1 left-1/2 -translate-x-1/2 -translate-y-full text-[10px] font-bold text-indigo-700 bg-white/90 px-1.5 py-0.5 rounded border border-indigo-200 whitespace-nowrap shadow-sm" data-id="element-12190">
                    {arrow.label}
                  </div>}
              </motion.div>;
      })}
        </div>}

      {/* Main line — pushed down to leave room for arrows */}
      <div className={`absolute ${cleanedArrows.length > 0 ? 'top-[70%]' : 'top-1/2'} left-0 right-0 h-1 bg-slate-300 rounded-full -translate-y-1/2`} data-id="element-12191" />

      {/* Start endpoint */}
      <div className={`absolute ${cleanedArrows.length > 0 ? 'top-[70%]' : 'top-1/2'} left-0 -translate-y-1/2 -translate-x-1/2 z-10`} data-id="element-12192">
        <div className="w-1 h-4 bg-slate-400 mx-auto" data-id="element-12193" />
        <div className="absolute top-4 left-1/2 -translate-x-1/2 text-xs font-bold text-slate-700 bg-white/90 px-1 py-0.5 rounded whitespace-nowrap" data-id="element-12194">
          {labelForTick(computed.min, 0)}
        </div>
      </div>

      {/* End endpoint */}
      <div className={`absolute ${cleanedArrows.length > 0 ? 'top-[70%]' : 'top-1/2'} right-0 -translate-y-1/2 translate-x-1/2 z-10`} data-id="element-12195">
        <div className="w-1 h-4 bg-slate-400 mx-auto" data-id="element-12196" />
        <div className="absolute top-4 left-1/2 -translate-x-1/2 text-xs font-bold text-slate-700 bg-white/90 px-1 py-0.5 rounded whitespace-nowrap" data-id="element-12197">
          {labelForTick(computed.max, computed.steps)}
        </div>
      </div>

      {/* Ticks (context only, no longer carry highlight state) */}
      {computed.ticks.map((value, i) => {
      const position = i / computed.steps * 100;
      const tickLabel = labelForTick(value, i);
      const isEndpoint = i === 0 || i === computed.steps;
      if (isEndpoint) return null;
      // Determine if this is a major tick (e.g. every 5th or 10th depending on steps)
      const isMajor = computed.steps > 20 ? i % 5 === 0 : true;
      return <motion.div key={`tick-${i}`} initial={{
        scaleY: 0,
        opacity: 0
      }} animate={{
        scaleY: 1,
        opacity: 1
      }} transition={{
        delay: i * 0.02,
        type: 'spring',
        stiffness: 400,
        damping: 25
      }} className={`absolute ${cleanedArrows.length > 0 ? 'top-[70%]' : 'top-1/2'} -translate-y-1/2 -translate-x-1/2 flex flex-col items-center`} style={{
        left: `${position}%`
      }} data-id="element-12198">
            <div className={`w-0.5 ${isMajor ? 'h-3 bg-slate-400' : 'h-2 bg-slate-300'} mx-auto`} data-id="element-12199" />
            {isMajor && <div className="absolute top-4 left-1/2 -translate-x-1/2 text-[10px] font-semibold whitespace-nowrap text-slate-500" data-id="element-12200">
                {tickLabel}
              </div>}
          </motion.div>;
    })}

      {/* Highlight markers — positioned at TRUE value (not snapped) */}
      {cleanedHighlights.map((value, hi) => {
      const clamped = clamp(value);
      const position = projectPct(clamped, computed.min, computed.max);
      const isStimulusHighlight = mode === 'stimulus';
      return <motion.div key={`hl-${hi}`} initial={{
        scale: 0,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} transition={{
        delay: 0.1 + hi * 0.05,
        type: 'spring',
        stiffness: 400,
        damping: 22
      }} className={`absolute ${cleanedArrows.length > 0 ? 'top-[70%]' : 'top-1/2'} -translate-y-1/2 -translate-x-1/2 z-20`} style={{
        left: `${position}%`
      }} data-id="element-12201">
            <div className="w-6 h-6 rounded-full bg-indigo-600 ring-4 ring-indigo-200 shadow-xl mx-auto" data-id="element-12202" />
            <motion.div initial={{
          opacity: 0,
          y: -8
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.25 + hi * 0.05
        }} className={`absolute -top-12 left-1/2 -translate-x-1/2 ${isStimulusHighlight ? 'bg-indigo-500' : 'bg-indigo-600'} text-white px-3 py-1.5 rounded-xl text-sm font-bold shadow-xl whitespace-nowrap z-20`} data-id="element-12203">
              {isStimulusHighlight ? '?' : formatValue(value)}
              <div className={`absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-[5px] border-r-[5px] border-t-[5px] border-transparent ${isStimulusHighlight ? 'border-t-indigo-500' : 'border-t-indigo-600'}`} data-id="element-12204" />
            </motion.div>
          </motion.div>;
    })}
    </div>;
  // ── STIMULUS MODE ──────────────────────────────────────────────────────
  if (mode === 'stimulus') {
    return <div className="bg-blue-50 rounded-xl px-6 pt-4 pb-10 border border-blue-200" data-id="element-12205">
        {label && <p className="text-sm font-semibold text-blue-800 mb-3 text-center" data-id="element-12206">
            {label}
          </p>}
        {numberLineViz}
      </div>;
  }
  // ── SUPPORT MODE ───────────────────────────────────────────────────────
  return <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-8 rounded-2xl border-2 border-blue-200 shadow-sm" data-id="element-12207">
      {label && <div className="mb-8 text-center" data-id="element-12208">
          <div className="inline-flex items-center gap-3 bg-white/90 rounded-xl px-5 py-3 border border-blue-200 shadow-sm" data-id="element-12209">
            <div className="w-2.5 h-2.5 rounded-full bg-indigo-600 animate-pulse" data-id="element-12210" />
            <span className="text-base font-bold text-blue-900" data-id="element-12211">{label}</span>
          </div>
        </div>}

      <div className="mb-8" data-id="element-12212">{numberLineViz}</div>

      <div className="space-y-4" data-id="element-12213">
        <div className="bg-white/80 rounded-xl p-5 border border-blue-100 shadow-sm" data-id="element-12214">
          <div className="flex items-start gap-3" data-id="element-12215">
            <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0" data-id="element-12216">
              <span className="text-indigo-600 text-lg" data-id="element-12217">📊</span>
            </div>
            <div data-id="element-12218">
              <h4 className="text-sm font-bold text-indigo-900 mb-2" data-id="element-12219">
                {explanationTitle}
              </h4>
              <p className="text-sm text-slate-700 leading-relaxed" data-id="element-12220">
                {explanationText}
                {cleanedHighlights.length > 0 && <span className="block mt-2 text-indigo-700 font-semibold" data-id="element-12221">
                    De blauwe punt{cleanedHighlights.length > 1 ? 'en' : ''}{' '}
                    laat zien waar{' '}
                    {cleanedHighlights.map((h, i) => <span key={i} data-id="element-12222">
                        <strong data-id="element-12223">{formatValue(h)}</strong>
                        {i < cleanedHighlights.length - 1 && (i === cleanedHighlights.length - 2 ? ' en ' : ', ')}
                      </span>)}{' '}
                    {cleanedHighlights.length > 1 ? 'liggen' : 'ligt'}.
                  </span>}
              </p>
            </div>
          </div>
        </div>

        {cleanedHighlights.length > 0 && <div className="bg-emerald-50/80 rounded-xl p-5 border border-emerald-200 shadow-sm" data-id="element-12224">
            <div className="flex items-start gap-3" data-id="element-12225">
              <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-12226">
                <span className="text-emerald-600 text-lg" data-id="element-12227">💡</span>
              </div>
              <div data-id="element-12228">
                <h4 className="text-sm font-bold text-emerald-900 mb-2" data-id="element-12229">
                  Hoe vind je dit getal?
                </h4>
                <p className="text-sm text-slate-700 leading-relaxed" data-id="element-12230">
                  {cleanedHighlights.map((h, i) => {
                const stepsFromStart = computed.stepSize > 0 ? (h - computed.min) / computed.stepSize : 0;
                const wholeSteps = Number.isInteger(stepsFromStart) ? stepsFromStart : Number(stepsFromStart.toFixed(2));
                return <span key={i} className="block mb-2 last:mb-0" data-id="element-12231">
                        <strong className="text-emerald-700" data-id="element-12232">
                          {formatValue(h)}
                        </strong>{' '}
                        ligt{' '}
                        <strong data-id="element-12233">
                          {wholeSteps}{' '}
                          {Math.abs(wholeSteps) === 1 ? 'stap' : 'stappen'}
                        </strong>{' '}
                        vanaf het begin.
                        {wholeSteps !== 0 && <span className="text-slate-600" data-id="element-12234">
                            {' '}
                            ({labelForTick(computed.min, 0)} + {wholeSteps} ×{' '}
                            {formatTick(computed.stepSize, computed.maxFractionDigits)}{' '}
                            = {formatValue(h)})
                          </span>}
                      </span>;
              })}
                </p>
              </div>
            </div>
          </div>}
      </div>
    </div>;
}